import { useEffect, useCallback, useRef } from 'react';
import { shouldRefreshTokens, getTokenExpiryRemaining } from '../utils/tokenCache';

/**
 * Hook to automatically refresh tokens before they expire
 * Checks tokens periodically and refreshes them when needed
 */
export function useTokenRefresh(refreshFunction: () => Promise<boolean>, enabled: boolean = true) {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  const checkAndRefreshTokens = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingRef.current) {
      return;
    }

    // Check if tokens need refresh (within 5 minutes of expiry)
    if (shouldRefreshTokens(5)) {
      isRefreshingRef.current = true;
      try {
        console.log('🔄 Auto-refreshing tokens before expiry...');
        await refreshFunction();
      } catch (error: any) {
        // Silently handle errors related to Amplify not being configured
        if (error?.message?.includes('Amplify has not been configured')) {
          console.warn('⚠️ Skipping token refresh - Amplify not configured yet');
        } else {
          console.error('❌ Auto token refresh failed:', error);
        }
      } finally {
        isRefreshingRef.current = false;
      }
    }
  }, [refreshFunction]);

  useEffect(() => {
    if (!enabled) {
      // Clear interval if disabled
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Check immediately (with a small delay to ensure Amplify is configured)
    setTimeout(() => {
      checkAndRefreshTokens();
    }, 100);

    // Calculate interval based on token expiry time
    // Check every minute, or half of the remaining token lifetime (whichever is smaller)
    const setupInterval = () => {
      const remaining = getTokenExpiryRemaining();
      
      if (remaining > 0) {
        // Check every minute, or every 30 seconds if tokens expire soon
        const intervalMs = remaining < 10 * 60 * 1000 // Less than 10 minutes remaining
          ? 30 * 1000 // Check every 30 seconds
          : 60 * 1000; // Check every minute
        
        // Clear existing interval
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }

        // Set up new interval
        refreshIntervalRef.current = setInterval(() => {
          checkAndRefreshTokens();
        }, intervalMs);

        console.log(`⏰ Token refresh check interval set to ${intervalMs / 1000} seconds`);
      }
    };

    setupInterval();

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [enabled, checkAndRefreshTokens]);

  return {
    checkAndRefresh: checkAndRefreshTokens
  };
}

